<template>
	<template v-if="!isTouchInput || isMultiple">
		<BaseAutoComplete
			ref="baseAutoRef"
			v-bind="{ ...$attrs, ...searchFieldProps(searchTerm, ($attrs.errorMessages ?? $attrs['error-messages'] ?? []) as string | string[]) }"
			:custom-filter="() => true"
			:loading="isLoading"
			:items="listOfProjects"
			item-title="name"
			:chip-editable="true"
			clearable
			v-model:search="searchTerm"
			no-data-text="No projects found."
			@update:modelValue="(value) => onChange(value)"
			@focus="() => items.length === 0 && refresh()"
			@edit-chip="handleChipEdit"
			@keydown.enter="handleEnterKey">
			<template #item="{ props, item }">
				<v-list-item v-bind="props" :title="item.raw.name"></v-list-item>
			</template>
			<template #no-data>
				<div v-if="hasSearch" class="d-flex align-center justify-center no-data-slot pb-2">
					<v-btn
						id="create-project-button"
						class="elevation-0 text-none create-project-btn create-action-btn"
						color="transparent"
						@mousedown.prevent
						@click.stop="createNewProject">
						Create project '<span class="create-action-query">{{ searchTerm }}</span
						>'
					</v-btn>
				</div>
				<v-list-item v-else class="pl-2">
					<v-list-item-title>No projects found.</v-list-item-title>
				</v-list-item>
			</template>
			<template #append-item v-if="shouldShowCreateButton && listOfProjects.length > 0">
				<v-list-item :key="'create-btn'" class="create-project-fixed pa-0" @mousedown.prevent @click.stop="createNewProject">
					<div class="text-center pt-0 pb-2 w-100">
						<hr class="mb-2" />
						<v-btn
							id="create-project-button"
							class="elevation-0 pa-2 text-none create-project-btn create-action-btn"
							color="transparent"
							:ripple="false"
							style="pointer-events: none">
							Create project '<span class="create-action-query">{{ searchTerm }}</span
							>'
						</v-btn>
					</div>
				</v-list-item>
			</template>
			<template #append-inner>
				<!-- stop mousedown so the autocomplete menu doesn't toggle -->
				<span @mousedown.stop.prevent @click.stop="handleAppendClick">
					<v-tooltip location="top">
						<template #activator="{ props: tooltipProps }">
							<div v-bind="tooltipProps">
								<v-icon
									v-if="!isDisabled && !isMultiple"
									v-test="'project-create-or-edit'"
									color="primary"
									class="cursor-pointer"
									>{{ appendIcon }}</v-icon
								>
							</div>
						</template>
						<span class="text-no-wrap">{{ tooltipText }}</span>
					</v-tooltip>
				</span>
			</template>
		</BaseAutoComplete>
	</template>
	<template v-else>
		<v-text-field
			ref="touchSearchFieldRef"
			v-bind="{ ...$attrs, ...searchFieldProps(touchSearchValue, ($attrs.errorMessages ?? $attrs['error-messages'] ?? []) as string | string[]) }"
			data-test-id="project-touch-search-input"
			clearable
			:loading="isLoading"
			:model-value="touchSearchValue"
			@update:model-value="onTouchSearchInput"
			@focus="onTouchSearchFocus"
			@keydown.enter.prevent="onTouchSearchEnter"
			@blur="onTouchSearchBlur">
			<!-- Bold the trailing (Required)/(Optional) tag on the label (LSP-16873).
			     The desktop BaseAutoComplete branch already does this via FieldLabel;
			     the touch field renders a raw v-text-field, so route its $attrs.label
			     through the same FieldLabel #label slot. No-op for tag-less labels. -->
			<template v-if="$attrs.label && !$slots.label" #label>
				<FieldLabel :label="String($attrs.label)" />
			</template>
			<template #append-inner>
				<span @mousedown.stop.prevent @click.stop="handleAppendClick">
					<v-tooltip location="top">
						<template #activator="{ props: tooltipProps }">
							<div v-bind="tooltipProps">
								<v-icon
									v-if="!isDisabled && !isMultiple"
									v-test="'project-create-or-edit'"
									color="primary"
									class="cursor-pointer"
									>{{ appendIcon }}</v-icon
								>
							</div>
						</template>
						<span class="text-no-wrap">{{ tooltipText }}</span>
					</v-tooltip>
				</span>
				<!-- LSP-17690: the touch branch above swaps the real v-autocomplete (which draws its own
				     dropdown chevron for free) for this plain v-text-field, silently dropping the chevron.
				     Mirrors LuckyPackAutoComplete's touch caret (LSP-16443 rework): a clickable
				     icon-caret-down that rotates open/closed and toggles the inline predictions. -->
				<span
					v-test="'project-touch-caret'"
					class="project-touch-caret"
					:class="{ 'project-touch-caret-open': showTouchPredictions }"
					role="button"
					:tabindex="isDisabled ? -1 : 0"
					:aria-expanded="showTouchPredictions"
					aria-label="Toggle project list"
					@mousedown.stop.prevent
					@click.stop="toggleTouchPredictions"
					@keydown.enter.stop.prevent="toggleTouchPredictions"
					@keydown.space.stop.prevent="toggleTouchPredictions">
					<v-icon aria-hidden="true">icon-caret-down</v-icon>
				</span>
			</template>
		</v-text-field>
		<div v-if="showTouchPredictions" data-test-id="project-touch-results" class="project-touch-results-list">
			<v-list density="compact">
				<v-list-item v-if="isLoading" data-test-id="project-touch-loading" class="px-3 py-2">
					<v-list-item-title class="text-medium-emphasis">Loading projects...</v-list-item-title>
				</v-list-item>
				<template v-else-if="listOfProjects.length > 0 || shouldShowCreateButton">
					<v-list-item
						v-for="project in listOfProjects"
						:key="project.id"
						data-test-id="project-touch-result-item"
						:active="project.id === selectedProject?.id"
						density="compact"
						class="px-3 py-2"
						@mousedown.prevent
						@click="selectTouchProject(project)">
						<template #append>
							<v-icon v-if="project.id === selectedProject?.id" color="primary" aria-label="Current project"
								>mdi-check</v-icon
							>
						</template>
						<v-list-item-title>{{ project.name }}</v-list-item-title>
					</v-list-item>
					<v-list-item
						v-if="shouldShowCreateButton"
						data-test-id="project-touch-create"
						density="compact"
						class="px-3 py-2"
						@mousedown.prevent
						@click="createTouchProject">
						<div v-if="assignmentMode" class="w-100 text-center py-1">
							<div class="text-primary font-weight-semibold text-truncate">Create project “{{ searchTerm }}”</div>
							<div class="text-body-2 text-medium-emphasis">Creates the project and adds this session to it</div>
						</div>
						<v-list-item-title v-else class="text-truncate">Create project '{{ searchTerm }}'</v-list-item-title>
					</v-list-item>
				</template>
				<v-list-item v-else data-test-id="project-touch-no-data" class="px-3 py-2">
					<v-list-item-title>No projects found.</v-list-item-title>
				</v-list-item>
			</v-list>
		</div>
	</template>
	<CreateProjectDialog ref="createProjectDialog" />
</template>
<script setup lang="ts">
	import { searchFieldProps } from "@/helpers/searchLimit";
	import BaseAutoComplete from "@/components/formfields/BaseAutoComplete.vue";
	import type { Project } from "@/types/Project";
	import { Ref, getCurrentInstance, computed, ref, watch, nextTick, onBeforeUnmount } from "vue";
	import api from "@/api";
	import { useAsyncAutocompleteItems } from "@/composables/useAsyncAutocompleteItems";
	import CreateProjectDialog from "@/components/CreateProjectDialog.vue";
	import { Conversation } from "@/types/Conversation";
	import DeviceUtility from "@/helpers/device";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";

	const instance = getCurrentInstance();

	type ProjectModelValue = Project | Project[] | null | undefined;
	type ProjectAutocompleteAttrs = {
		"model-value"?: ProjectModelValue;
		modelValue?: ProjectModelValue;
		value?: ProjectModelValue;
		multiple?: boolean;
		disabled?: boolean;
	};
	type ProjectChangeEvent = "update:modelValue" | "input" | "change";
	type ProjectAutocompleteProxy = {
		$attrs: ProjectAutocompleteAttrs;
		$emit: (event: ProjectChangeEvent, value: Project | null) => void;
		$root?: {
			$confirm?: {
				open: (...args: unknown[]) => Promise<boolean>;
			};
		};
	};

	const componentProxy = instance?.proxy as unknown as ProjectAutocompleteProxy | null;

	function getAttrs(): ProjectAutocompleteAttrs {
		return componentProxy?.$attrs ?? {};
	}

	function getModelValue(): ProjectModelValue {
		const attrs = getAttrs();
		return attrs["model-value"] ?? attrs.modelValue ?? attrs.value;
	}

	function emitProjectChange(event: ProjectChangeEvent, value: Project | null) {
		componentProxy?.$emit(event, value);
	}

	const props = withDefaults(
		defineProps<{
			entity?: Conversation | null;
			assignmentMode?: boolean;
		}>(),
		{
			entity: null,
			assignmentMode: false
		}
	);

	const { items, isLoading, searchTerm, onSearchInput, refresh } = useAsyncAutocompleteItems<Project>({
		fetchItems: async (term: string, signal?: AbortSignal) => {
			// LSP-15828 — /projects/lite is a narrow picker endpoint that
			// inner-joins project_shares + skips the heavy Customer / Label /
			// GeoAddress includes carried by GET /projects. Keeps the Live
			// Call project autocomplete responsive on orgs with thousands of
			// projects. The composable's abort signal is forwarded so stale
			// in-flight requests get cancelled when the user keeps typing.
			const resp = await api.getProjectsLite(
				{
					pageSize: props.assignmentMode ? 25 : 10,
					where: term ? { name: { $ilike: `%${term}%` } } : {}
				},
				{ signal }
			);
			return resp.rows as Project[];
		},
		getSelected: () => {
			return getModelValue();
		}
	});

	const listOfProjects = items as Ref<Project[]>;

	const isMultiple = computed<boolean>(() => getAttrs().multiple === true);
	const isTouchInput = computed<boolean>(() => props.assignmentMode || DeviceUtility.isMobile || DeviceUtility.isTablet);
	const selectedProject = computed<Project | null>(() => {
		const selected = getModelValue();
		return Array.isArray(selected) ? null : (selected ?? null);
	});
	const isDisabled = computed<boolean>(() => !!getAttrs().disabled);

	const hasSearch = computed<boolean>(
		() => !!(searchTerm as Ref<string>).value && ((searchTerm as Ref<string>).value || "").trim().length > 0
	);
	const shouldShowCreateButton = computed<boolean>(() => {
		if (isDisabled.value) return false;
		const term = ((searchTerm as Ref<string>).value || "").trim();
		if (!term) return false;
		const lower = term.toLowerCase();
		return !(listOfProjects.value || []).filter(Boolean).some((project) => (project?.name || "").trim().toLowerCase() === lower);
	});

	const hasSingleSelection = computed<boolean>(() => !isMultiple.value && !!selectedProject.value && !!selectedProject.value.id);
	const appendIcon = computed<string>(() => (hasSingleSelection.value ? "icon-pencil-edit-solid" : "icon-plus"));
	const tooltipText = computed<string>(() => (hasSingleSelection.value ? "Edit project details." : "Create a new project."));

	type CreateProjectDialogExpose = {
		open: (defaultName?: string) => Promise<Project | null>;
		editProject: (id: string) => Promise<Project | null>;
	};
	const createProjectDialog = ref<CreateProjectDialogExpose | null>(null);
	const baseAutoRef = ref<{ blur: () => void } | null>(null);
	const touchSearchFieldRef = ref<{ blur?: () => void; focus?: () => void } | null>(null);
	const showTouchPredictions = ref(false);
	const touchBlurTimer = ref<ReturnType<typeof setTimeout> | null>(null);
	const touchSearchValue = computed<string>(() => {
		const currentSearchTerm = (searchTerm as Ref<string>).value;
		return currentSearchTerm || selectedProject.value?.name || "";
	});

	async function handleChipEdit(project: Project) {
		const dialog = createProjectDialog.value;
		if (!dialog) return;
		const record = await dialog.editProject(project.id);
		if (record) {
			updateItems(record);
			emitProjectChange("update:modelValue", record);
		}
	}

	async function handleAppendClick() {
		if (isDisabled.value) return;
		if (hasSingleSelection.value) {
			const project = selectedProject.value;
			if (!project) return;
			const dialog = createProjectDialog.value;
			if (!dialog) return;
			const record = await dialog.editProject(project.id);
			if (record) {
				updateItems(record);
				emitProjectChange("update:modelValue", record);
			}
		} else {
			await createNewProject();
		}
	}

	function updateItems(item: Project) {
		const existing = listOfProjects.value || [];
		const existingIndex = existing.findIndex((project) => project.id === item.id);
		if (existingIndex !== -1) {
			listOfProjects.value = [...existing.slice(0, existingIndex), item, ...existing.slice(existingIndex + 1)];
		} else {
			listOfProjects.value = [item, ...existing];
		}
	}

	function handleEnterKey() {
		if (!isDisabled.value && hasSearch.value && listOfProjects.value.length === 0) {
			createNewProject();
		}
	}

	function clearTouchBlurTimer() {
		if (!touchBlurTimer.value) return;
		clearTimeout(touchBlurTimer.value);
		touchBlurTimer.value = null;
	}

	function onTouchSearchFocus() {
		// Always clear a pending blur-close timer, even on the early-return path below — otherwise a
		// blur that's still within its 150ms debounce window when focus comes back would go on to close
		// predictions the user just reopened.
		clearTouchBlurTimer();
		// toggleTouchPredictions (LSP-17690) opens by calling this directly, then focuses the field so
		// the keyboard comes up — that programmatic .focus() re-fires this same @focus handler a second
		// time. Stay idempotent while already open so the caret path issues a single refresh(), not two
		// back-to-back calls (the first immediately aborted). Mirrors LuckyPackAutoComplete's handleFocus.
		if (showTouchPredictions.value) return;
		showTouchPredictions.value = true;
		// Always refetch on focus. Gating on an empty list suppressed the fetch
		// whenever a project was preselected — the selectedProject watcher seeds that
		// one project into listOfProjects, so the inline dropdown showed ONLY the
		// selected item instead of the full project list (LSP-16441 regression).
		refresh();
	}

	function toggleTouchPredictions() {
		if (isDisabled.value) return;
		if (showTouchPredictions.value) {
			showTouchPredictions.value = false;
		} else {
			onTouchSearchFocus();
			touchSearchFieldRef.value?.focus?.();
		}
	}

	function onTouchSearchEnter() {
		if (isDisabled.value) return;
		// Mirror the desktop BaseAutoComplete Enter behaviour: a search term with no
		// exact match creates the project. shouldShowCreateButton encodes exactly that.
		if (shouldShowCreateButton.value) {
			createTouchProject();
		}
	}

	async function onTouchSearchInput(text: string | null | undefined) {
		clearTouchBlurTimer();
		showTouchPredictions.value = true;

		const nextSearchTerm = text ?? "";
		onSearchInput(nextSearchTerm);
		if (nextSearchTerm === "") {
			await onChange(null);
		}
	}

	function onTouchSearchBlur() {
		clearTouchBlurTimer();
		touchBlurTimer.value = setTimeout(() => {
			showTouchPredictions.value = false;
			touchBlurTimer.value = null;
		}, 150);
	}

	async function selectTouchProject(project: Project) {
		clearTouchBlurTimer();
		showTouchPredictions.value = false;
		(searchTerm as Ref<string>).value = project.name || "";
		await onChange(project);
		touchSearchFieldRef.value?.blur?.();
	}

	async function createTouchProject() {
		clearTouchBlurTimer();
		showTouchPredictions.value = false;
		await createNewProject();
		touchSearchFieldRef.value?.blur?.();
	}

	async function createNewProject() {
		if (isDisabled.value) return;
		const defaultName = ((searchTerm as Ref<string>).value || "").trim();
		const dialog = createProjectDialog.value;
		if (!dialog) return;
		const record = await dialog.open(defaultName);
		if (record) {
			const existing = listOfProjects.value || [];
			const exists = existing.some((project) => project.id === record.id);
			listOfProjects.value = exists ? existing : [record, ...existing];
			await onChange(record);
		}
		(searchTerm as Ref<string>).value = "";
		await nextTick();
		baseAutoRef.value?.blur?.();
		touchSearchFieldRef.value?.blur?.();
	}

	async function onChange(newValue: Project | null) {
		const oldValue = selectedProject.value;

		// LSP-18561: the media-removal confirm ("You're about to change or remove a project…") must fire
		// ONLY when the user is dissociating a project that is actually SAVED on a real entity — i.e. an
		// existing conversation whose stored projectId matches the project being changed away from. The
		// creation flows (Home → new live call / recording / photo request, all passing :entity="null")
		// have no saved media to dissociate, so the warning must never appear there.
		//
		// The previous guard compared `oldValue?.id !== props.entity?.projectId` directly. That collapses
		// when both sides are `undefined` (e.g. an entity without a saved projectId and an oldValue object
		// missing an id): `undefined !== undefined` is false, which slipped past the guard and fired the
		// confirm on a flow that has nothing to dissociate. Requiring a truthy saved projectId that matches
		// the outgoing project scopes the warning to the genuine dissociation case only.
		const savedProjectId = props.entity?.projectId;
		const isDissociatingSavedProject = !!savedProjectId && oldValue?.id === savedProjectId;

		if (oldValue === newValue || oldValue === null || !isDissociatingSavedProject) {
			emitProjectChange("update:modelValue", newValue);
			emitProjectChange("input", newValue);
			emitProjectChange("change", newValue);
			return;
		}

		const confirmed = await componentProxy?.$root?.$confirm?.open(
			"You're about to change or remove a project",
			"Are you sure? This session's media may be attached to a checklist. The media will be removed from the project and associated checklists.",
			false,
			"Update",
			"Cancel",
			true,
			false,
			{ width: "600px" }
		);

		if (confirmed) {
			emitProjectChange("update:modelValue", newValue);
			emitProjectChange("input", newValue);
			emitProjectChange("change", newValue);
		} else {
			// Revert to the old value if confirmation was cancelled
			emitProjectChange("update:modelValue", oldValue);
			emitProjectChange("input", oldValue);
			emitProjectChange("change", oldValue);
		}
	}

	// Ensure the currently selected project is always present in the dropdown list
	watch(selectedProject, (proj) => {
		if (!proj) return;
		const existing = listOfProjects.value || [];
		const exists = existing.some((project) => project.id === proj.id);
		if (!exists) {
			listOfProjects.value = [proj, ...existing];
		}
	});

	onBeforeUnmount(clearTouchBlurTimer);

	defineExpose({
		blur: () => (isTouchInput.value && !isMultiple.value ? touchSearchFieldRef.value?.blur?.() : baseAutoRef.value?.blur?.()),
		open: () => {
			if (isTouchInput.value && !isMultiple.value) {
				onTouchSearchFocus();
				touchSearchFieldRef.value?.focus?.();
			}
		}
	});
</script>
<style scoped>
	.create-project-fixed {
		position: sticky !important;
		bottom: 0;
		background-color: white;
		z-index: 1;
	}

	.create-project-btn {
		white-space: normal;
		overflow-wrap: break-word;
	}

	.create-project-btn:hover {
		background-color: transparent !important;
	}

	.create-project-btn:hover::before {
		opacity: 0 !important;
	}

	.create-project-fixed:hover,
	.create-project-fixed:hover::after {
		background-color: color-mix(in srgb, rgb(var(--v-theme-on-surface)) calc(var(--v-hover-opacity) * 100%), white);
	}

	/* Inline touch results (iPad/iPhone). Without a border/shadow the open list blends into the page
	   (LSP-18562). Match LuckyPackAutoComplete's inline touch list so this dropdown reads as an overlay
	   menu — bordered, surface-filled, rounded, and elevated — consistent with the app's other menus. */
	.project-touch-results-list {
		overflow-y: auto;
		max-height: min(18rem, calc(100dvh - 22rem));
		border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
		background: rgb(var(--v-theme-surface));
		border-radius: 8px;
		box-shadow:
			0 5px 5px -3px rgba(0, 0, 0, 0.2),
			0 8px 10px 1px rgba(0, 0, 0, 0.14),
			0 3px 14px 2px rgba(0, 0, 0, 0.12);
	}

	@media (orientation: landscape) and (max-height: 600px) {
		.project-touch-results-list {
			max-height: calc(100dvh - 10rem);
		}
	}

	.project-touch-caret {
		cursor: pointer;
	}

	/* Rotate the dropdown caret ⌄ → ︿ while the inline predictions are open, matching the desktop
	   v-autocomplete (LSP-17690, mirrors LuckyPackAutoComplete's LSP-16443 rework). */
	.project-touch-caret :deep(.icon-caret-down) {
		transition: transform 0.2s ease;
	}

	.project-touch-caret.project-touch-caret-open :deep(.icon-caret-down) {
		transform: rotate(180deg);
	}
</style>
